// ── THE ONE-PAGE ARC/PLC ELECTION SHEET ──────────────────────────────────────
//
// Kevin, 2026-09-18: *"We do not need base acres to answer 'which program has the
// stronger payment under these assumptions?' Base acres are needed when we want to
// turn the answer into estimated total farm dollars. And we don't necessarily need
// to store PLC payment yields just to make a useful one-page advisory — we can
// print the farm's breakeven payment yield and let the farmer compare it to the
// FSA number."*
//
// That is the whole product. This sheet carries, in his order: the ARC-CO scenario
// payment, the PLC payment-yield breakeven, price sensitivity, what ARC-CO actually
// paid, and the irrigation assumption where the county is split.
//
// ⚠ IT STATES A THRESHOLD, NEVER AN ELECTION. Base acres and producer share cancel
// out of the comparison; the PLC PAYMENT YIELD DOES NOT. With it unknown the honest
// output is "PLC pays more than ARC-CO here if your FSA payment yield exceeds X" —
// a number to hold against a document the grower already has. It becomes a
// recommendation only when the actual payment yield is supplied, and that value is
// typed, never stored and never guessed. It is a separate FSA determination: not
// the APH, not the actual harvested yield, not the county benchmark.
import { resolvePracticeRow, countyRead, settledArcPerAcre, enrolRead, electionLabel, BELOW_SWITCH_ARC, belowSwitchArc } from "./arcPlcElection";

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));

// FSA's own 2026-09-15 release. The election window is the reason this sheet exists
// now rather than in the spring, and a sheet that does not carry the deadline is a
// sheet somebody reads in January.
export const ELECTION_WINDOWS = {
  2026: { opens: "2026-09-16", closes: "2026-12-11" },
  2027: { opens: "2026-11-02", closes: "2027-03-15" },
};

// What every ARC/PLC surface says when the active program year's FSA figures are not published
// (Kevin, 2026-09-19 — his words). Nothing from another year is computed in its place.
export function arcPlcPendingMessage(programYear) {
  return `${programYear} ARC/PLC analysis pending — FSA has announced the ${programYear} election period but has not yet published the program data required for this analysis.`;
}

// The program-year picker's option text, off cropYear.js electionYearContext's `offered` entry.
// A closed election is labelled PREVIOUS so it is never read as the one being made now.
const _MO = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const _d = (iso) => { const [y, m, d] = String(iso).split("-").map(Number); return `${_MO[m]} ${d}, ${y}`; };
export function arcPlcYearOptionLabel(o) {
  if (!o) return "";
  const pend = o.published ? "" : " — FSA data pending";
  if (o.status === "current") return `${o.year} program year (current election)${pend}`;
  if (o.status === "open") return `${o.year} program year (election also open)${pend}`;
  if (o.status === "previous") return `${o.year} program year — previous (election closed ${_d(o.window.closes)})${pend}`;
  return `${o.year} program year (election opens ${_d(o.window.opens)})${pend}`;
}

// ── WHICH CROP THIS ELECTION IS FOR (2026-09-18) ─────────────────────────────
// Kevin: "Make sure it's clearly labeled which crop year we are recommending for." The
// program year IS the crop year, and for WHEAT that is the trap: the 2026 wheat crop was
// harvested before the 2026 election opened, while the wheat going in the ground now is the
// 2027 crop, under a separate election (FSA, 2026-09-15 — the windows above). The tracker
// said "crop in the ground now" for every crop, which is wrong for wheat.
// USDA marketing years, as WASDE states them: wheat, barley and oats June–May; corn,
// soybeans and sorghum September–August. The MYA is a projection until that year ends, and
// ARC/PLC pay from October of the year after the crop.
const MY_START_MONTH = { WHEAT: 6, BARLEY: 6, OATS: 6, CORN: 9, SOYBEANS: 9, SORGHUM: 9 };
const MONTH = ["", "January", "February", "March", "April", "May", "June", "July", "August",
               "September", "October", "November", "December"];
export function cropYearLabel(crop, programYear) {
  const y = Number(programYear), c = String(crop || "").toUpperCase(), m = MY_START_MONTH[c];
  if (!Number.isInteger(y) || y < 2000 || !m) return null;
  const name = c.toLowerCase();
  const next = ELECTION_WINDOWS[y + 1];
  return {
    programYear: y,
    crop: y + " " + name + " crop (harvested " + (m === 6 ? "summer " : "fall ") + y + ")",
    marketingYear: MONTH[m] + " " + y + " – " + MONTH[m - 1] + " " + (y + 1),
    myaFinal: MONTH[m - 1] + " " + (y + 1),
    pays: "October " + (y + 1),
    // Only a crop harvested BEFORE its own election opens can be confused with the next one.
    nextCrop: m === 6 ? name.charAt(0).toUpperCase() + name.slice(1) + " planted this fall is the " + (y + 1)
      + " crop — a separate election" + (next ? ", " + next.opens + " to " + next.closes : "") : null,
  };
}

// The price ladder the sensitivity table walks.
//
// ⚠ IT RUNS DOWN FROM THE REFERENCE PRICE, NOT OUT FROM THE FORECAST. PLC's rate is
// max(0, reference − MYA), so at or above the effective reference price it is $0.00
// at every price — and with the 2026 forecasts sitting above reference, a ladder
// centred on the forecast spent four of its seven rows printing the same row of
// zeros. Every row below reference says something different; every row above it
// says the same nothing. So the ladder is anchored at reference and walks DOWN,
// which is also the only direction in which this election is worth anything.
//
// The forecast is always included even when it sits above reference, because "where
// FSA thinks the price is going" is the row the grower looks for first.
export function priceLadder(mya, erp) {
  const m = num(mya), e = num(erp);
  const anchor = e != null ? e : m;
  if (anchor == null) return [];
  // Sized to the crop's own scale, so a $12 soybean ladder does not step like a $4
  // corn one.
  const step = Math.max(0.05, Math.round((anchor * 0.08) * 20) / 20);
  const out = [];
  for (let i = 0; i <= 5; i++) {
    const p = Math.round((anchor - i * step) * 100) / 100;
    if (p > 0) out.push(p);
  }
  if (m != null && !out.some((p) => Math.abs(p - m) < 0.005)) out.push(Math.round(m * 100) / 100);
  return [...new Set(out)].sort((a, b) => a - b);
}

// One row of the sensitivity table: what each program pays per base acre at a price,
// and the payment yield at which they cross. ⚠ THE ARITHMETIC IS countyRead's — the
// same function the Coverage Advisor and the Full Strategy Report call — so this sheet
// cannot price a county differently from the screen it was printed from.
const asScenario = (r) => (r && r.usable && r.price != null && r.arcRate != null ? {
  price: r.price, arcRate: r.arcRate, plcRate: r.plcRate, arcPerBaseAcre: r.arcPerAcre,
  breakeven: r.breakeven, capped: r.capped, arcYield: r.arcYield, yieldBasis: r.yieldBasis,
} : null);
export function scenarioAt({ price, countyRow, ref, yieldFactor }) {
  if (num(price) == null) return null;
  return asScenario(countyRead({ countyRow, ref, price, yieldFactor }));
}

// County yield as a share of its own FSA benchmark: short, normal, long.
export const YIELD_SCENARIOS = [0.85, 1, 1.15];

// The whole sheet as DATA. The renderer below turns it into a page; a checker can
// drive this without a browser, and every refusal is a named reason rather than a
// blank.
export function buildArcPlcSheet({
  crop, state, county, programYear,
  practiceRows,          // { ALL: row } or { IRRIGATED: row, NONIRRIGATED: row }
  irrigatedPct,          // 0-100, or null when unanswered
  ref,                   // arc_plc_reference row for programYear
  settledRow,            // the most recent SETTLED county row (payment_rate published)
  settledYear,
  paymentYield,          // OPTIONAL, typed. Never stored, never defaulted.
  yieldFactor,           // county yield vs its own benchmark, as the SCREEN uses it; null =
                         // the yield FSA published, else a normal year (countyRead)
  asOf,
}) {
  const res = resolvePracticeRow(practiceRows, irrigatedPct);
  const win = ELECTION_WINDOWS[programYear] || null;
  const base = {
    crop, state, county, programYear, asOf: asOf || new Date().toISOString().slice(0, 10),
    window: win, practice: res, ref: ref || null,
    sourceAsOf: (ref && ref.source_asof) || null,
    yieldFactor: num(yieldFactor),
  };
  // ⚠ A SPLIT COUNTY WITH NO ANSWER PRINTS THE QUESTION, NOT A FIGURE. Both FSA rows
  // go on the sheet so the agent can see what the answer is worth, and nothing is
  // computed from one of them.
  if (!res.row)
    return { ...base, usable: false,
             reason: res.refused || "no county figures published for this crop and year",
             scenarios: [], headline: null, settled: null };
  const myaForecast = num(ref && ref.mya_price);
  // THE HEADLINE IS countyRead AT FSA'S OWN FORECAST — the call the Coverage Advisor and
  // the Full Strategy Report make, with the same inputs — and it IS the ladder's forecast
  // row, so the table and the headline above it cannot disagree either.
  const head = countyRead({ countyRow: res.row, ref, yieldFactor: base.yieldFactor, paymentYield });
  const atForecast = asScenario(head);
  const scenarios = priceLadder(myaForecast, ref && ref.effective_ref_price)
    .map((p) => (atForecast && Math.abs(p - atForecast.price) < 0.005 ? atForecast
      : scenarioAt({ price: p, countyRow: res.row, ref, yieldFactor: base.yieldFactor })))
    .filter(Boolean);
  const read = head.usable
    ? { state: head.state, breakeven: head.breakeven, pick: head.pick, arcPerAcre: head.arcPerAcre, plcPerAcre: head.plcPerAcre }
    : { state: "unknown", breakeven: null, pick: null };

  // What ARC-CO actually paid, where FSA has settled a year. One real outcome beats
  // a modelled series, and the absence of earlier years is NAMED rather than padded.
  const settled = settledRow && num(settledRow.payment_rate) != null ? {
    year: settledYear,
    rate: num(settledRow.payment_rate),
    perBaseAcre: settledArcPerAcre(settledRow),
    benchmarkRevenue: num(settledRow.benchmark_revenue),
    actualRevenue: num(settledRow.actual_revenue),
  } : null;

  // ⚠ THE BREAKEVEN IS THE PRODUCT, SO IT GETS A RANGE AND A RULER.
  // A column of thresholds — 28, 35, 47, 70, 72 — is a curve the reader has to
  // rebuild in their head, and no single one of them means anything alone. The range
  // says how wide the question is; the county's own benchmark yield is the only
  // published figure here that gives it scale, and it is a ruler, NOT a stand-in for
  // the farm's payment yield.
  const bes = scenarios.filter((x) => x.breakeven != null && isFinite(x.breakeven));
  const span = bes.length ? {
    lo: bes.reduce((a, b) => (b.breakeven < a.breakeven ? b : a)),
    hi: bes.reduce((a, b) => (b.breakeven > a.breakeven ? b : a)),
    countyBenchmark: num(res.row.benchmark_yield),
  } : null;

  // ── PRICE x COUNTY YIELD — SCENARIOS, NOT FORECASTS (work order E) ────────
  // The ladder above varies the price at one county yield. ARC-CO moves with the COUNTY
  // yield as well, and nobody knows the 2026 one yet — so the grid sets the price ladder
  // against the county coming in short, normal and long of its own FSA benchmark. Every
  // cell is countyRead, the same call as the headline; none is a forecast of anything.
  //
  // ONE TABLE: the headline's own county yield is a column of it (the ladder above, cell for
  // cell), not a second table repeating it — the repeat is what pushed the sheet to two pages.
  const bench = num(res.row.benchmark_yield);
  const headFactor = head.yieldBasis === "scenario" ? base.yieldFactor
    : head.yieldBasis === "benchmark" ? 1
    : (bench && head.arcYield != null ? head.arcYield / bench : null);
  const pct = (f) => Math.round(f * 100) + "%";
  const cols = [{ head: true, factor: headFactor,
    label: head.yieldBasis === "published" ? "FSA county yield"
      : head.yieldBasis === "scenario" ? "Yield Model " + pct(headFactor) : "County yield 100%" }];
  for (const f of YIELD_SCENARIOS)
    if (!(headFactor != null && Math.abs(f - headFactor) < 0.005)) cols.push({ head: false, factor: f, label: "County yield " + pct(f) });
  cols.sort((a, b) => (a.factor != null ? a.factor : 1) - (b.factor != null ? b.factor : 1));
  const grid = {
    cols: cols.map((c) => ({ label: c.label, head: c.head })),
    rows: scenarios.map((x) => ({ price: x.price, plcRate: x.plcRate,
      cells: cols.map((c) => (c.head ? x : scenarioAt({ price: x.price, countyRow: res.row, ref, yieldFactor: c.factor }))) })),
  };

  // Which to enroll in: electionOutlook's switch yield, the number the report prints (#208).
  const enrol = enrolRead({ countyRow: res.row, ref, paymentYield });
  return { ...base, usable: true, reason: null, scenarios, atForecast, read, settled, enrol,
           span, grid, paymentYield: num(paymentYield) };
}


// ── THE PAGE ─────────────────────────────────────────────────────────────────
//
// Kevin, on the first Livingston corn sheet: *"looks like it could be better …
// make it simple, powerful, easy to read. Not so wordy."*
//
// So: one number leads, a chart carries the shape, the table backs it up, and the
// prose is cut to what a grower cannot work out from the figures. Everything that
// was an explanatory paragraph is either a four-word label or gone.
const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const d2 = (n) => (n == null ? "—" : "$" + Number(n).toFixed(2));
const bu = (n) => (n == null ? "—" : Math.round(Number(n)).toLocaleString() + " bu");
const title = (s) => String(s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// ⚠ THE RELATIONSHIP IS A CURVE AND A COLUMN OF NUMBERS HIDES IT. The breakeven
// rises as price falls and then turns back — ARC-CO stops at 12% of benchmark
// revenue while PLC's rate keeps climbing. Drawn, that is instant.
function curveHtml(s) {
  const pts = s.scenarios.filter((x) => x.breakeven != null && isFinite(x.breakeven));
  if (pts.length < 2) return "";
  const W = 460, H = 178, L = 34, R = 96, T = 16, B = 24;
  const xs = pts.map((x) => x.price);
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const bench = s.span && s.span.countyBenchmark != null ? s.span.countyBenchmark : null;
  const py = s.paymentYield;
  const yMax = Math.max(...pts.map((p) => p.breakeven), bench || 0, py || 0) * 1.15;
  const X = (p) => L + ((p - x0) / (x1 - x0 || 1)) * (W - L - R);
  const Y = (v) => T + (1 - Math.min(v, yMax) / yMax) * (H - T - B);
  const line = pts.map((p, i) => (i ? "L" : "M") + X(p.price).toFixed(1) + " " + Y(p.breakeven).toFixed(1)).join(" ");
  const area = line + ` L ${X(xs[xs.length - 1]).toFixed(1)} ${T} L ${X(xs[0]).toFixed(1)} ${T} Z`;
  const grid = [0, 0.5, 1].map((f) => {
    const v = yMax * f, y = Y(v);
    return `<line x1="${L}" y1="${y.toFixed(1)}" x2="${W - R}" y2="${y.toFixed(1)}" stroke="#e6ebe2"/>
      <text x="${L - 4}" y="${(y + 3).toFixed(1)}" text-anchor="end" class="ax">${Math.round(v)}</text>`;
  }).join("");
  const xlab = [x0, x1].map((p, i) =>
    `<text x="${X(p).toFixed(1)}" y="${H - 7}" text-anchor="${i ? "end" : "start"}" class="ax">$${p.toFixed(2)}</text>`).join("");
  const ref = (v, colour, label, weight) => v == null ? "" :
    `<line x1="${L}" y1="${Y(v).toFixed(1)}" x2="${W - R}" y2="${Y(v).toFixed(1)}" stroke="${colour}" stroke-width="${weight}" ${weight < 1.5 ? 'stroke-dasharray="4 3"' : ""}/>
     <text x="${W - R + 5}" y="${(Y(v) + 3).toFixed(1)}" class="ax" fill="${colour}">${esc(label)}</text>`;
  return `<div class="chart">
    <svg viewBox="0 0 ${W} ${H}" width="100%">
      ${grid}
      <path d="${area}" fill="#e9f0f6" stroke="none"/>
      <path d="${line}" fill="none" stroke="#1f4e6b" stroke-width="2.4" stroke-linejoin="round"/>
      ${ref(bench, "#8a958a", "county " + Math.round(bench || 0) + " bu", 1)}
      ${ref(py, "#8a6414", "yours " + Math.round(py || 0) + " bu", 1.8)}
      ${xlab}
      <text x="${L - 4}" y="${T - 5}" text-anchor="end" class="ax">bu</text>
    </svg>
    <div class="chart-k">shaded = PLC pays more</div>
  </div>`;
}

function heroHtml(s) {
  const r = s.read || {}, sp = s.span, e = s.enrol || null;
  // ⚠ ONE NUMBER, NEVER A RANGE — AND THE SAME NUMBER THE REPORT PRINTS. This headline read
  // "PLC beats ARC-CO above 63–148 bu/ac", the spread of crossovers across the price ladder.
  // Kevin, 2026-09-18: "a wild range and weird thing to say. If it falls within that range?
  // What does that even mean?" The one payment yield that IS a threshold is electionOutlook's
  // switch yield (enrolRead): above it PLC pays at least as much at every price — ON A NORMAL
  // COUNTY CROP. It takes ARC-CO's revenue as benchmark yield × price, so a short county crop
  // raises ARC-CO past it (Genesee wheat at 85%: 140 bu at $5.85 against a 77 bu switch), and
  // the page must say so beside a grid that shows it. It is what
  // the Full Strategy Report prints (#208), so the sheet and the report cannot name two
  // numbers for one county. Where no such number exists the headline says so; the table
  // carries each price's own crossover, one price and one number per row.
  const ruler = sp && sp.countyBenchmark != null ? ` County benchmark ${bu(sp.countyBenchmark)} — a ruler, not your payment yield.` : "";
  const mya = d2(s.ref && s.ref.mya_price);
  const atForecast = r.state === "decided"
    ? `At FSA's ${mya} forecast: ${esc(r.pick)} pays more.`
    : r.state && r.state !== "unknown" ? `At FSA's ${mya} forecast: ${esc(electionLabel({ ...r, usable: true }))}.` : "";
  if (e && e.usable && e.switchYield != null) {
    const n = Math.round(e.switchYield);
    // Below the switch there is no winner to stamp: ARC-CO leads only over a band of prices.
    if (e.lean === "ARC-CO")
      return hero("arc", "At your " + bu(e.paymentYield) + " payment yield",
        `Below the <b>${n}</b> bu/ac switch`,
        `${esc(belowSwitchArc(e.arcWinsBetween))} (on a normal county crop); PLC's is larger at other prices below the reference price. A short county crop raises ARC-CO; the grid shows how far. ${atForecast}`);
    if (e.lean)
      return hero(e.lean === "PLC" ? "plc" : "arc", "At your " + bu(e.paymentYield) + " payment yield",
        `Enroll in <b>${esc(e.lean)}</b>`,
        `Your payment yield is ${e.lean === "PLC" ? "above" : "below"} ${n} bu — the payment yield above which PLC pays at least as much as ARC-CO at every price when the county harvests a normal crop. A short county crop raises ARC-CO; the grid shows how far. ${atForecast}`);
    // ⚠ THE APH DISTINCTION STAYS BESIDE THE NUMBER, not only in the footer. It is the one
    // caveat that changes what a grower looks up.
    return hero("thr", "Your FSA PLC payment yield decides this — not your APH",
      `Enroll in <b>PLC</b> if your payment yield is above <b>${n}</b> bu/ac; below it, ${esc(BELOW_SWITCH_ARC)}`,
      `Above ${n} bu, PLC pays at least as much as ARC-CO at every price when the county harvests a normal crop (its FSA benchmark yield). Below it, ${esc(BELOW_SWITCH_ARC)}. A short county crop raises ARC-CO — the only one of the two that pays on one — and the grid shows how far. ${atForecast}${ruler}`);
  }
  // ARC-CO triggers first (corn, soybeans): the recommendation is ARC-CO down to one price line
  // set by the payment yield, never "no single number" with nothing to act on.
  if (e && e.usable && e.switchReason && e.arcFirst) {
    const d2x = (v) => "$" + Number(v).toFixed(2);
    const why = `ARC-CO starts paying at ${d2x(e.arcTriggerPrice)}, above PLC's ${d2x(e.plcTriggerPrice)} reference price, so it pays first as prices fall — and it is the only one of the two that pays on a short county crop.`;
    if (e.lean && e.plcBelow != null)
      return hero(e.lean === "PLC" ? "plc" : "arc", "At your " + bu(e.paymentYield) + " payment yield",
        `Enroll in <b>${esc(e.lean)}</b>`,
        `PLC pays more only if the season-average price falls below <b>${d2x(e.plcBelow)}</b> at your ${bu(e.paymentYield)} payment yield (on a normal county crop). ${why} ${atForecast}`);
    const lines = (e.lines || []).map((l) => `<b>${d2x(l.below)}</b> at ${l.paymentYield} bu`).join(" · ");
    return hero("arc", "Your FSA PLC payment yield sets the line — not your APH",
      `Enroll in <b>ARC-CO</b> unless the price falls below your payment yield's line`,
      `${why} PLC pays more only if the season-average price falls below the line for your payment yield (on a normal county crop): ${lines}. A lower payment yield lowers the line. ${atForecast}${ruler}`);
  }
  // No county benchmark to build the threshold from: the forecast read alone.
  if (r.state === "neither_pays")
    return hero("none", `At FSA's ${mya} forecast`, "Neither program pays",
      "This election is cover for a lower price." + (sp ? " The table shows, price by price, the payment yield PLC would have to beat." : ""));
  if (r.state === "arc_only")
    return hero("arc", "PLC cannot pay at this price",
      `<b>ARC-CO</b> pays at this price`,
      `The price is at or above the ${d2(s.ref && s.ref.effective_ref_price)} reference price, so PLC can't pay at any payment yield.`);
  if (r.state === "plc_only")
    return hero("plc", "ARC-CO does not trigger here",
      `<b>PLC</b> pays at this price`,
      "County revenue is above the 90% guarantee.");
  return hero("none", esc(title(s.county)) + " " + esc(String(s.crop || "").toLowerCase()),
    "Not enough published county data", "FSA has not published the figures this comparison needs.");
}
const hero = (kind, kicker, big, sub) => `<div class="hl hl-${kind}">
  <div class="hl-k">${kicker}</div>
  <div class="hl-t">${big}</div>
  <div class="hl-s">${sub}</div>
</div>`;

export function renderArcPlcSheet(s) {
  if (!s.usable)
    return page(s, heroHtml(s) + `<div class="hl hl-none"><div class="hl-t">Sheet not ready</div>
      <div class="hl-s">${esc(s.reason)}</div></div>${s.practice && s.practice.split ? bothRowsHtml(s) : ""}`);


  return page(s, `
    ${heroHtml(s)}
    <div class="grid2">
      <section class="chartcol">${curveHtml(s)}</section>
      <section>
        <table class="kv">
          <tr><td>Marketing-year price</td><td class="n">${d2(s.ref && s.ref.mya_price)} <span class="mut">${esc((s.ref && s.ref.mya_status) || "")}</span></td></tr>
          <tr><td>Reference price</td><td class="n">${d2(s.ref && s.ref.effective_ref_price)}</td></tr>
          <tr><td>County benchmark yield</td><td class="n">${bu(s.practice.row.benchmark_yield)}</td></tr>
        </table>
        ${s.practice.split ? irrHtml(s) : ""}
      </section>
    </div>

    ${gridHtml(s)}
  `);
}

// THE ONE TABLE: price ladder down the side, county yield across. Each cell leads with
// the payment yield PLC must beat — the product. No dollars: Kevin, 2026-11-01, "Do not
// include dollars in ARC/PLC data" — prices stay, payment amounts never print;
// a dash is a price at which PLC cannot pay at all. The headline's own column is marked,
// and the forecast row is the headline itself.
function gridHtml(s) {
  const g = s.grid;
  if (!g || !(g.rows || []).length) return "";
  const cell = (c, head) => !c ? `<td class="n mut">—</td>`
    : `<td class="n${head ? " hd" : ""}"><b>${c.breakeven == null ? "—" : c.breakeven === 0 ? "PLC" : bu(c.breakeven)}</b></td>`;
  const body = g.rows.map((r) => {
    const here = s.atForecast && Math.abs(r.price - s.atForecast.price) < 0.005;
    return `<tr class="${here ? "now" : ""}"><td class="em">${d2(r.price)}${here ? ' <span class="tag">forecast</span>' : ""}</td>`
      + `${r.cells.map((c, i) => cell(c, g.cols[i] && g.cols[i].head)).join("")}</tr>`;
  }).join("");
  return `<table class="sens gridt">
      <thead><tr><th>If the price lands at</th>${g.cols.map((c) => `<th class="n${c.head ? " hd" : ""}">${esc(c.label)}${c.head ? " ◆" : ""}</th>`).join("")}</tr></thead>
      <tbody>${body}</tbody>
    </table>
    <p class="note">Each cell: the payment yield PLC must beat. Scenarios, not forecasts. ◆ is the headline's column; a dash means PLC can't pay at that price.</p>`;
}

function irrHtml(s) {
  const p = s.practice;
  if (!p.blended)
    return `<p class="note warn"><b>County split by irrigation.</b> FSA blends the two by this farm's irrigated share (7 CFR 1412.53(c)). Not answered, so no combined figure is shown.</p>${bothRowsHtml(s)}`;
  return `<p class="note warn"><b>Assumes ${Math.round(p.irrigatedPct)}% irrigated.</b> FSA blends the two county rows by the farm's own share (7 CFR 1412.53(c)). If the farm's actual share differs, the figures move.</p>`;
}

function bothRowsHtml(s) {
  const p = s.practice || {};
  if (!p.irrigated || !p.nonIrrigated) return "";
  return `<table class="kv split">
    <tr><th></th><th class="n">Irrigated</th><th class="n">Non-irr</th></tr>
    <tr><td>Benchmark yield</td><td class="n">${bu(p.irrigated.benchmark_yield)}</td><td class="n">${bu(p.nonIrrigated.benchmark_yield)}</td></tr>
  </table>`;
}

function page(s, body) {
  const cty = title(s.county), cropL = title(s.crop), win = s.window;
  const cyl = cropYearLabel(s.crop, s.programYear);
  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>ARC/PLC Election Sheet — ${esc(cty)} ${esc(cropL)} ${esc(s.programYear)}</title>
<style>
  @page { size: letter portrait; margin: 0.5in; }
  * { box-sizing: border-box; }
  /* ⚠ THE PREVIEW HAS TO LOOK LIKE THE PRINT. @page handles paper, but the agent
     opens this in a browser window first, where no margin applies and the figures
     run to the edge of the glass. Padding here, reset for print so the margin is
     not applied twice. */
  body { font: 10pt/1.4 -apple-system, "Segoe UI", Helvetica, Arial, sans-serif; color: #1c2a1a; margin: 0; padding: 0.5in; }
  @media print { body { padding: 0; } }
  .head { border-bottom: 2.5px solid #1a2e1a; padding-bottom: 6px; margin-bottom: 10px; display: flex; align-items: flex-end; gap: 12px; }
  .head h1 { font-size: 15pt; margin: 0; letter-spacing: -.01em; flex: 1; }
  .head .r { text-align: right; font-size: 8pt; color: #5c6f5c; }
  .head .r b { color: #8a3324; display: block; font-size: 9pt; }
  .hl { border-radius: 6px; padding: 11px 14px; margin-bottom: 10px; border-left: 5px solid #8e9a87; background: #f2f5f0; }
  .hl-thr { border-left-color: #1f4e6b; background: #eef4f8; }
  .hl-arc { border-left-color: #2d6a4f; background: #eaf3ec; }
  .hl-plc { border-left-color: #8a6414; background: #f8f2e2; }
  .hl-k { font-size: 7.5pt; text-transform: uppercase; letter-spacing: .1em; font-weight: 800; color: #6d7a66; }
  .hl-t { font-size: 17pt; font-weight: 800; line-height: 1.15; margin: 2px 0 3px; letter-spacing: -.015em; }
  .hl-t b { color: #1f4e6b; font-size: 22pt; }
  .hl-plc .hl-t b, .hl-arc .hl-t b { color: inherit; font-size: inherit; }
  .hl-s { font-size: 9pt; color: #45543e; }
  .grid2 { display: flex; gap: 14px; margin-bottom: 10px; align-items: flex-start; }
  .grid2 > section { flex: 1; min-width: 0; }
  .grid2 > .chartcol { flex: 1.3; }
  .chart { border: 1px solid #e3e9df; border-radius: 6px; padding: 5px 7px 2px; background: #fbfdfa; }
  .chart-k { font-size: 7pt; color: #8a958a; text-align: right; margin-top: -2px; }
  text.ax { font-size: 7.5px; fill: #8a958a; font-family: inherit; }
  table { border-collapse: collapse; width: 100%; }
  .kv td, .kv th { padding: 3px 0; font-size: 9pt; border-bottom: 1px solid #edf1ea; }
  .kv th { font-size: 7pt; text-transform: uppercase; letter-spacing: .05em; color: #8a958a; text-align: right; }
  .sens th { font-size: 7.5pt; text-transform: uppercase; letter-spacing: .05em; color: #6d7a66; text-align: left; background: #eef2ec; padding: 4px 8px; }
  .sens td { padding: 4px 8px; font-size: 9.5pt; border-bottom: 1px solid #edf1ea; }
  .sens tr.now td { background: #fdf8e8; font-weight: 700; }
  .gridt td { padding: 2px 8px; font-size: 9pt; }
  .gridt .sub { font-size: 7.5pt; color: #6d7a66; font-weight: 400; }
  .gridt .hd { background: #f3f7fa; }
  .n { text-align: right; font-variant-numeric: tabular-nums; }
  .em { font-weight: 700; }
  .mut { color: #8a958a; font-size: 8pt; }
  .tag { font-size: 6.5pt; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; background: #e3e9df; color: #5c6f5c; padding: 1px 4px; border-radius: 2px; vertical-align: 1px; }
  .note { font-size: 8pt; color: #6d7a66; margin: 4px 0 10px; }
  .note.warn { background: #f2f7fa; border: 1px dashed #1f4e6b; border-radius: 4px; padding: 5px 7px; color: #29414c; margin-top: 7px; }
  .dollars { border: 1px solid #d7ded2; border-radius: 6px; padding: 8px 12px; background: #fafcf9; }
  .dol-t { font-size: 9.5pt; font-weight: 800; margin-bottom: 5px; }
  .formula { font-size: 9pt; font-weight: 700; text-align: center; padding: 5px; background: #fff; border: 1px solid #e3e9df; border-radius: 4px; color: #45543e; }
  .blank { display: flex; align-items: flex-end; gap: 8px; margin-top: 8px; font-size: 8.5pt; color: #6d7a66; }
  .rule { flex: 1; border-bottom: 1px solid #677167; height: 13px; }
  .rule.short { flex: 0 0 60px; }
  .cyl { font-size: 8.5pt; color: #33402f; background: #fbf7ea; border: 1px solid #e8dcb5; border-radius: 5px; padding: 4px 8px; margin: -4px 0 8px; }
  .foot { border-top: 1px solid #d7ded2; margin-top: 10px; padding-top: 6px; font-size: 7.5pt; color: #8a958a; line-height: 1.45; }
</style></head><body>
<div class="head">
  <h1>${esc(cty)} ${esc(cropL)} — ${esc(s.programYear)} crop ARC/PLC election</h1>
  <div class="r">${esc(s.state)} · FSA data${s.sourceAsOf ? " " + esc(s.sourceAsOf) : ""}
    ${win ? `<b>Elect by ${esc(win.closes)}</b>` : ""}</div>
</div>
${cyl ? `<div class="cyl">For your <b>${esc(cyl.crop)}</b> · marketing year ${esc(cyl.marketingYear)}, so the price is a USDA projection until ${esc(cyl.myaFinal)} · any payment arrives ${esc(cyl.pays)}${cyl.nextCrop ? ". <b>" + esc(cyl.nextCrop) + ".</b>" : ""}</div>` : ""}
${body}
<div class="foot">
  <b>PLC payment yield</b> is a separate FSA determination — not your APH, not your harvested yield, not the county benchmark. Silas does not hold it and does not guess it.
  <b>Election is not enrollment:</b> you must enroll by signed contract every year, or the farm is ineligible even with an election on file.
  <b>ARC-IC</b> pays on 65% of base acres against 85%, so it must beat the county rate by about 1.31×.
  Guarantee 90% of benchmark, cap 12% of benchmark revenue (7 CFR 1412); irrigated blending per 1412.53(c).
</div>
</body></html>`;
}
